// src/components/PolygonToolbar.js
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardHeader,
  Stack,
  TextField,
} from "@mui/material";
import { Pentagon as PentagonIcon } from "@mui/icons-material";
import { Polygon } from "fabric";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";

const PolygonToolbar = ({ canvas }) => {
  const { selectedObject, setSelectedObject, canvasScale, setSnackbar } =
    useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [sides, setSides] = useState(5); // Default to pentagon

  // Sync sides with selected polygon's points
  useEffect(() => {
    if (selectedObject && selectedObject.type === "polygon") {
      const numSides = selectedObject.points ? selectedObject.points.length : 5;
      setSides(numSides >= 3 ? numSides : 3);
      console.log("PolygonToolbar: Synced sides", { numSides });
    }
  }, [selectedObject]);

  const generatePolygonPoints = (numSides, radius) => {
    const points = [];
    for (let i = 0; i < numSides; i++) {
      const angle = (i * 2 * Math.PI) / numSides - Math.PI / 2; // Start from top
      points.push({
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      });
    }
    return points;
  };

  const addPolygon = useCallback(() => {
    if (!canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "Canvas or scale not initialized.",
        severity: "error",
      });
      return;
    }

    if (sides < 3) {
      setSnackbar({
        open: true,
        message: "Polygon must have at least 3 sides.",
        severity: "error",
      });
      return;
    }

    console.log("PolygonToolbar: Checking fabric.Polygon", {
      Polygon: Polygon,
    });
    if (!Polygon) {
      console.error("PolygonToolbar: fabric.Polygon is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add polygon. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      const radius = 50;
      const points = generatePolygonPoints(sides, radius);

      const polygon = new Polygon(points, {
        top: canvas.getHeight() / 2,
        left: canvas.getWidth() / 2,
        fill: "#4CAF50",
        stroke: "black",
        strokeWidth: 2,
        strokeUniform: true,
        originX: "center",
        originY: "center",
        preserveObjectStacking: false,
        type: "polygon",
      });

      canvas.add(polygon);
      canvas.setActiveObject(polygon);
      setSelectedObject(polygon);
      canvas.requestRenderAll();
      saveHistory(canvas); // Save history for new polygon
      console.log("PolygonToolbar: Polygon added", { sides });
      setSnackbar({
        open: true,
        message: `Polygon with ${sides} sides added.`,
        severity: "success",
      });
    } catch (error) {
      console.error("PolygonToolbar: Failed to add polygon", error);
      setSnackbar({
        open: true,
        message: "Error adding polygon. Please try again.",
        severity: "error",
      });
    }
  }, [canvas, canvasScale, sides, setSelectedObject, setSnackbar, saveHistory]);

  const handleSidesChange = (e) => {
    const newSides = Math.max(3, parseInt(e.target.value) || 3); // Enforce minimum of 3
    setSides(newSides);

    if (selectedObject && selectedObject.type === "polygon") {
      try {
        // Calculate new points with the same radius
        const radius = 50; // Match addPolygon radius
        const newPoints = generatePolygonPoints(newSides, radius);
        selectedObject.set({ points: newPoints });
        selectedObject.setCoords(); // Update bounding box
        canvas.requestRenderAll();
        setSelectedObject(selectedObject); // Update context
        canvas.fire("object:modified", { target: selectedObject }); // Trigger event for history
        saveHistory(canvas); // Save history for sides change
        console.log("PolygonToolbar: Sides updated", {
          sides: newSides,
          points: newPoints.length,
        });
        setSnackbar({
          open: true,
          message: `Polygon updated to ${newSides} sides.`,
          severity: "success",
        });
      } catch (error) {
        console.error("PolygonToolbar: Failed to update sides", error);
        setSnackbar({
          open: true,
          message: "Error updating polygon sides. Please try again.",
          severity: "error",
        });
      }
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Card
          elevation={4}
          sx={{
            backgroundColor: "white",
            color: "#0d2445",
            height: 80,
            width: 80,
            marginBottom: 1,
            textAlign: "center",
            ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
          }}
        >
          <CardActionArea
            sx={{ "& .MuiTouchRipple-ripple": { color: "blue" } }}
            onClick={addPolygon}
          >
            <PentagonIcon sx={{ marginTop: 2, marginBottom: -1 }} />
            <CardHeader
              title="Polygon"
              sx={{
                "& .MuiCardHeader-title": {
                  color: "#000",
                  fontSize: 12,
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  width: 60,
                  marginLeft: -1,
                },
              }}
            />
          </CardActionArea>
        </Card>
        <TextField
          label="Number of Sides"
          type="number"
          value={sides}
          onChange={handleSidesChange}
          inputProps={{ min: 3, "aria-label": "Number of polygon sides" }}
          size="small"
          sx={{ width: 120 }}
          error={sides < 3}
          helperText={sides < 3 ? "Minimum 3 sides" : ""}
        />
      </Stack>
    </Box>
  );
};

export default PolygonToolbar;
